import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useServices } from 'lib/hooks/queries/services/useServices';
import useModal from 'lib/hooks/useModal';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import EmptyDataRows from 'components/shared/AgTablePage/components/EmptyDataRows';

import AddServicesModal from './components/AddServicesModal/AddServicesModal';
import BulkActions from './components/BulkActions';
import EditServiceModal from './components/EditServiceModal';
import { scheduleOfFeesMainColumns } from './lib/helpers';

const ScheduleOfFees = ({
  addFooterActions,
  setHeaderButtonProps,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  const { id: practiceId } = useParams();
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedRows, setSelectedRows] = useState([]);
  const gridApiRef = useRef(null);

  const columnDefs = useMemo(() => scheduleOfFeesMainColumns, []);

  const handleClearSelection = useCallback(() => {
    gridApiRef.current?.deselectAll();
    setSelectedRows([]);
  }, []);

  const handleSelectionChanged = useCallback(({ api }) => {
    gridApiRef.current = api;
    setSelectedRows(api?.getSelectedNodes() ?? []);
  }, []);

  useEffect(() => {
    setHeaderButtonProps({
      text: 'Add services',
      onClick: openModal,
      icon: 'add-square-mono'
    });

    addFooterActions([
      {
        text: 'Continue',
        onClick: () => {
          mutateOrchestratorData({});
        },
        isDisabled: orchestratorLoading
      }
    ]);
  }, [
    orchestratorLoading,
    openModal,
    setHeaderButtonProps,
    addFooterActions,
    mutateOrchestratorData
  ]);

  const renderEmptyState = useCallback(
    () => (
      <EmptyDataRows
        title="No services added yet"
        description="Add services to define your practice's schedule of fees"
        emptyTextButton="Add services"
      />
    ),
    []
  );

  return (
    <div className="mb-4 flex flex-1 flex-col">
      <AgTablePage
        defaultColumns={columnDefs}
        useData={useServices}
        title="Services"
        name="services"
        noHeader
        rowStyle={{ cursor: 'pointer' }}
        queryParams={{ forPractice: false, getPayers: true, practiceId: Number(practiceId) }}
        queryOptions={{ refetchOnMount: 'always' }}
        ModalComponent={EditServiceModal}
        autoHeight
        className="overflow-y-auto rounded-2xl border border-solid border-neutral-200"
        noRowsOverlayComponent={renderEmptyState}
        onSelectionChanged={handleSelectionChanged}
      />

      <BulkActions selectedRows={selectedRows} onClearSelection={handleClearSelection} />

      {isOpen && <AddServicesModal isOpen={isOpen} onClose={closeModal} />}
    </div>
  );
};

export default ScheduleOfFees;
